/**
 * ErrorFallback - Reusable Error Fallback Component
 *
 * Provides consistent error UI for ErrorBoundary components.
 * Used to display user-friendly error messages when components fail to render.
 */

import { Box, Button, Text } from '@chakra-ui/react';
import type React from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';

interface ErrorFallbackProps {
  /** Section name for error message (e.g., "dates", "create project form") */
  section?: string;
  /** Optional reset handler */
  onReset?: () => void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({ section = 'section', onReset }) => {
  return (
    <Box
      p={LAYOUT_SPACING.CARD_PADDING}
      bg="red.50"
      border="1px solid"
      borderColor="red.200"
      borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
    >
      <Text color="red.700" fontWeight="medium" mb={2}>
        Error in {section}
      </Text>
      <Text color="red.600" fontSize="sm" mb={3}>
        Something went wrong. Please try refreshing the page or contact support.
      </Text>
      {onReset && (
        <Button size="sm" onClick={onReset} colorPalette="red">
          Try Again
        </Button>
      )}
    </Box>
  );
};
